/**
 * ChunkLoader - Async render chunk loading via <script> injection.
 *
 * The render chunk (sdk-render.js) is loaded asynchronously to keep
 * the initial SDK loader small. Uses <script> tag injection (not
 * dynamic import()) to avoid CORS issues when SDK runs on merchant
 * domains but chunk is served from CDN.
 */

export interface RenderModule {
  show(instruction: any, context: any): void;
  dismiss(id: string): void;
  updateContent(id: string, path: string, value: string): void;
}

export class ChunkLoader {
  private module: RenderModule | null = null;
  private loadPromise: Promise<RenderModule | null> | null = null;

  /**
   * Start loading the render chunk. Call immediately after SDK init.
   * The chunk self-registers on window.__hamlaRender when it executes.
   */
  prefetch(url: string): void {
    if (this.loadPromise) return; // Already loading
    if (typeof document === 'undefined') return; // SSR guard

    this.loadPromise = new Promise<RenderModule | null>((resolve) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;

      script.onload = () => {
        this.module = (window as any).__hamlaRender || null;
        resolve(this.module);
      };

      script.onerror = () => {
        // Retry once after 2s
        setTimeout(() => {
          const retry = document.createElement('script');
          retry.src = url;
          retry.async = true;
          retry.onload = () => {
            this.module = (window as any).__hamlaRender || null;
            resolve(this.module);
          };
          retry.onerror = () => resolve(null); // Give up silently
          document.head.appendChild(retry);
        }, 2000);
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Get the render module. Returns immediately if already loaded,
   * waits for load if in progress, returns null if failed.
   */
  async getRender(): Promise<RenderModule | null> {
    if (this.module) return this.module;
    if (this.loadPromise) return this.loadPromise;
    return null;
  }

  /** Check if render module is loaded (sync, no waiting) */
  isReady(): boolean {
    return this.module !== null;
  }
}
